import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './homepage/components/Navbar';
import Footer from './homepage/components/Footer';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ServiceStatus {
  name: string;
  status: string;
  response_ms?: number | null;
}

interface IncidentItem {
  id: string;
  title: string;
  severity: string;
  started_at: string;
  resolved_at: string | null;
  duration_minutes: number | null;
}

interface StatusData {
  overall_status: string;
  uptime_pct: number;
  last_checked: string | null;
  services: ServiceStatus[];
  recent_incidents: IncidentItem[];
}

const FALLBACK_DATA: StatusData = {
  overall_status: 'operational',
  uptime_pct: 99.98,
  last_checked: new Date().toISOString(),
  services: [
    { name: 'Web-App', status: 'operational' },
    { name: 'API', status: 'operational' },
    { name: 'Datenbank', status: 'operational' },
    { name: 'E-Mail', status: 'operational' },
    { name: 'Datei-Upload', status: 'operational' },
  ],
  recent_incidents: [],
};

/** Normalize API status values to our 3 display categories */
function normalizeOverall(s: string): 'operational' | 'degraded' | 'outage' {
  if (s === 'operational') return 'operational';
  if (s === 'degraded') return 'degraded';
  return 'outage'; // major_outage, outage, etc.
}

function normalizeServiceStatus(s: string): 'operational' | 'degraded' | 'outage' {
  if (s === 'operational') return 'operational';
  if (s === 'degraded' || s === 'warning') return 'degraded';
  return 'outage'; // down, error, etc.
}

const STATUS_CONFIG: Record<string, { color: string; bg: string; text: string; icon: string }> = {
  operational: { color: '#10b981', bg: '#ecfdf5', text: 'Betriebsbereit', icon: '\u2713' },
  degraded:    { color: '#f59e0b', bg: '#fffbeb', text: 'Eingeschränkt', icon: '!' },
  outage:      { color: '#ef4444', bg: '#fef2f2', text: 'Ausfall', icon: '\u2717' },
};

const OVERALL_CONFIG: Record<string, { color: string; bg: string; text: string }> = {
  operational: { color: '#10b981', bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', text: 'Alle Systeme laufen einwandfrei' },
  degraded:    { color: '#f59e0b', bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', text: 'Einige Systeme sind eingeschränkt' },
  outage:      { color: '#ef4444', bg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', text: 'Systemstörung erkannt' },
};

function formatTimestamp(iso: string | null): string {
  if (!iso) return '–';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function SkeletonLoader() {
  const skeletonLine = (width: string, height = 20): React.CSSProperties => ({
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: 6,
    width,
    height,
  });

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px' }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      <div style={{ ...skeletonLine('100%', 80), marginBottom: 32, borderRadius: 16 }} />
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', marginBottom: 8,
          background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
        }}>
          <div style={skeletonLine('40%', 18)} />
          <div style={skeletonLine('100px', 28)} />
        </div>
      ))}
      <div style={{ ...skeletonLine('100%', 120), marginTop: 32, borderRadius: 12 }} />
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  useEffect(() => {
    axios.get(`${API_BASE}/public/status`)
      .then((res) => setData(res.data))
      .catch(() => {
        setData(FALLBACK_DATA);
      })
      .finally(() => setLoading(false));
  }, []);

  const overallKey = data ? normalizeOverall(data.overall_status) : 'operational';
  const overall = OVERALL_CONFIG[overallKey];

  return (
    <div className="homepage" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1e3a5f 100%)',
        padding: '140px 24px 60px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
          fontSize: 36, fontWeight: 800, color: '#fff', margin: 0,
        }}>
          Systemstatus
        </h1>
        <p style={{
          fontFamily: "'Inter', -apple-system, sans-serif",
          fontSize: 16, color: 'rgba(255,255,255,0.7)', marginTop: 12,
        }}>
          Aktuelle Verfügbarkeit aller CoachDesk365-Dienste
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>
        {loading && <SkeletonLoader />}

        {!loading && error && (
          <div style={{
            background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '48px 32px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</div>
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
              fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px',
            }}>
              Status konnte nicht geladen werden
            </h2>
            <p style={{
              fontFamily: "'Inter', -apple-system, sans-serif",
              fontSize: 15, color: 'var(--text-muted)', margin: 0,
            }}>
              Bitte versuchen Sie es später erneut oder kontaktieren Sie uns unter{' '}
              <a href="mailto:service@coachdesk365.com" style={{ color: '#2563eb' }}>
                service@coachdesk365.com
              </a>
            </p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Overall Status Banner */}
            <div style={{
              background: overall.bg,
              borderRadius: 16,
              border: `2px solid ${overall.color}`,
              padding: '28px 32px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 32,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: overall.color, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {overallKey === 'operational' ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : overallKey === 'degraded' ? (
                  <span style={{ color: '#fff', fontWeight: 800, fontSize: 24 }}>!</span>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                )}
              </div>
              <div>
                <h2 style={{
                  fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
                  fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0,
                }}>
                  {overall.text}
                </h2>
                <p style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 14, color: 'var(--text-muted)', margin: '4px 0 0',
                }}>
                  Zuletzt geprüft: {formatTimestamp(data.last_checked)}
                </p>
              </div>
            </div>

            {/* Uptime Card */}
            <div style={{
              background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              padding: '20px 24px', marginBottom: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <p style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 13, color: 'var(--text-muted)', margin: 0, textTransform: 'uppercase',
                  letterSpacing: 0.5, fontWeight: 600,
                }}>
                  Verfügbarkeit (30 Tage)
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 200, height: 8, background: '#e5e7eb', borderRadius: 4,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${data.uptime_pct}%`, height: '100%',
                    background: data.uptime_pct >= 99.5 ? '#10b981'
                      : data.uptime_pct >= 98 ? '#f59e0b' : '#ef4444',
                    borderRadius: 4, transition: 'width 0.6s ease',
                  }} />
                </div>
                <span style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 20, fontWeight: 700, color: 'var(--text)',
                  minWidth: 70, textAlign: 'right',
                }}>
                  {data.uptime_pct.toFixed(2)}%
                </span>
              </div>
            </div>

            {/* Services List */}
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
              fontSize: 18, fontWeight: 700, color: 'var(--text)',
              margin: '0 0 16px',
            }}>
              Dienste
            </h3>
            <div style={{
              background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
              marginBottom: 40,
            }}>
              {data.services.map((svc, idx) => {
                const normalizedStatus = normalizeServiceStatus(svc.status);
                const cfg = STATUS_CONFIG[normalizedStatus];
                return (
                  <div key={svc.name} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 24px',
                    borderBottom: idx < data.services.length - 1 ? '1px solid #f3f4f6' : 'none',
                  }}>
                    <span style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 15, fontWeight: 500, color: 'var(--text)',
                    }}>
                      {svc.name}
                    </span>
                    <span style={{
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      fontSize: 13, fontWeight: 600,
                      color: cfg.color, background: cfg.bg,
                      padding: '6px 14px', borderRadius: 20,
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{ fontSize: 14, lineHeight: 1 }}>{cfg.icon}</span>
                      {cfg.text}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Recent Incidents */}
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
              fontSize: 18, fontWeight: 700, color: 'var(--text)',
              margin: '0 0 16px',
            }}>
              Letzte Vorfälle
            </h3>
            {data.recent_incidents.length === 0 ? (
              <div style={{
                background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                padding: '32px 24px', textAlign: 'center',
              }}>
                <p style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 15, color: 'var(--text-muted)', margin: 0,
                }}>
                  Keine Vorfälle in den letzten 30 Tagen
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {data.recent_incidents.map((inc) => {
                  const sevColor = inc.severity === 'critical' ? '#ef4444'
                    : inc.severity === 'major' ? '#f59e0b' : '#2563eb';
                  return (
                    <div key={inc.id} style={{
                      background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      padding: '20px 24px', borderLeft: `4px solid ${sevColor}`,
                    }}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: 8,
                      }}>
                        <h4 style={{
                          fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
                          fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: 0,
                        }}>
                          {inc.title}
                        </h4>
                        <span style={{
                          fontFamily: "'Inter', -apple-system, sans-serif",
                          fontSize: 13, color: 'var(--text-muted)',
                        }}>
                          {formatTimestamp(inc.started_at)}
                        </span>
                      </div>
                      <p style={{
                        fontFamily: "'Inter', -apple-system, sans-serif",
                        fontSize: 14, color: 'var(--text-light)', margin: 0, lineHeight: 1.6,
                      }}>
                        {inc.resolved_at
                          ? `Behoben am ${formatTimestamp(inc.resolved_at)}${inc.duration_minutes ? ` (Dauer: ${inc.duration_minutes} Min.)` : ''}`
                          : 'Noch nicht behoben'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Back Link */}
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <Link to="/" style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 14, color: '#2563eb', textDecoration: 'none',
                fontWeight: 500,
              }}>
                &larr; Zurück zur Startseite
              </Link>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
